import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { useEffect, useState } from "react";
import { formMappings } from "../../../../config/formMappings";
import { getFormEntries } from "../../../../services/apiService";
import { FormEntry } from "../../../../types/types";
import InternalPage from "../../../../components/layout/internal/InternalPage";
import AverageTablesPerRegistration from "./AverageTablesPerRegistration";
import BazaarInfoTable from "./BazaarInfoTable";
import TotalTableCount from "./TotalTableCount";
import TotalTableRevenue from "../FinancialReportPage/TotalTableRevenue";

// Module-level cache to prevent duplicate API calls across component instances
const bazaarEntriesCache = new Map<
  string,
  {
    data: FormEntry[];
    promise: Promise<FormEntry[]>;
  }
>();

export default function BazaarTableReportPage() {
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const isVersion1 = formMapping?.version === "1";
  const formId =
    formMapping && "bazaarTable" in formMapping
      ? formMapping.bazaarTable?.formId
      : undefined;
  const [v2Entries, setV2Entries] = useState<FormEntry[]>([]);

  // Make single API call for v2+ forms
  useEffect(() => {
    if (!isVersion1 && formId) {
      // Check if we have cached data or an ongoing request for this formId
      const cached = bazaarEntriesCache.get(formId);

      if (cached) {
        // If we have data, use it immediately
        if (cached.data.length > 0) {
          setV2Entries(cached.data);
          return;
        }

        // If there's an ongoing promise, wait for it
        if (cached.promise) {
          cached.promise
            .then((data) => {
              setV2Entries(data);
            })
            .catch(() => {
              // Error already handled by the original request
            });
          return;
        }
      }

      // Create new request and cache the promise immediately
      const promise = getFormEntries(formId)
        .then((data) => {
          // Update cache with data
          bazaarEntriesCache.set(formId, { data, promise });
          setV2Entries(data);
          return data;
        })
        .catch((error) => {
          console.error(
            `Error fetching form entries for form ID ${formId}:`,
            error
          );
          // Remove from cache on error so we can retry
          bazaarEntriesCache.delete(formId);
          throw error;
        });

      // Cache the promise immediately to prevent duplicate requests
      bazaarEntriesCache.set(formId, { data: [], promise });
    } else {
      // Reset entries if conditions aren't met
      setV2Entries([]);
    }
  }, [isVersion1, formId]);

  return (
    <InternalPage>
      <div>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-x-5 sm:gap-y-5">
          <TotalTableCount v2Entries={v2Entries} />
          <TotalTableRevenue v2Entries={v2Entries} />
          <AverageTablesPerRegistration v2Entries={v2Entries} />
        </dl>
        <dl className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-0 sm:gap-x-5 gap-y-5 h-[600px]">
          <BazaarInfoTable v2Entries={v2Entries} />
        </dl>
      </div>
    </InternalPage>
  );
}
